import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';

import { getMessageFromHeaders } from 'app/shared/api/headers';
import { FieldErrorVM, isProblemDetails } from 'app/shared/api/problem-details';
import { isFulfilledAction, isRejectedAction } from 'app/shared/reducers/reducer.utils';

type ToastMessage = {
  message?: string;
  key?: string;
};

const GENERIC_ERROR = 'Ocurrió un error inesperado.';

/**
 * Spanish copy for the generic problem keys this SPA raises itself, plus the HTTP ones the backend
 * sends as an `X-timoraApp-error` header.
 *
 * Domain errors are deliberately NOT listed: they arrive with their own `detail`, which the fallback
 * in {@link addErrorAlert} shows. Backend `errorKey`s belong to the mobile/web clients' catalogues,
 * and mirroring them here would be a second copy to keep in sync for no gain.
 */
const ERROR_MESSAGES: Record<string, string> = {
  'error.server.not.reachable': 'No se pudo contactar con el servidor.',
  'error.url.not.found': 'No se encontró el recurso solicitado.',
  'error.http.400': 'Solicitud incorrecta.',
  'error.http.403': 'No tienes permisos para acceder a esta página.',
  'error.http.404': 'La página no existe.',
  'error.http.405': 'El verbo HTTP utilizado no tiene soporte para esta URL.',
  'error.http.500': 'Error interno del servidor.',
  'error.concurrencyFailure': 'Otro usuario modificó estos datos al mismo tiempo que tú. Tus cambios fueron rechazados.',
  'error.validation': 'Error de validación en el servidor.',
};

const addErrorAlert = (message: ToastMessage) => {
  toast.error((message.key && ERROR_MESSAGES[message.key]) || message.message || GENERIC_ERROR);
};

/**
 * One toast per rejected bean-validation field.
 *
 * `fieldError.message` is the constraint's own text (`ExceptionControllerAdvice.getFieldErrors` sends
 * `getDefaultMessage()`, e.g. "size must be between 8 and 100"), so it leads; the field name is the
 * context. Index subscripts collapse, so a failure on `items[3].name` reads as `items[].name` — the
 * same shape the backend reports for the whole collection.
 */
const getFieldErrorsToasts = (fieldErrors: FieldErrorVM[]): ToastMessage[] =>
  fieldErrors.map(fieldError => {
    const field = fieldError.field.replace(/\[\d*\]/g, '[]');
    return { message: `Error en el campo "${field}": ${fieldError.message}` };
  });

// eslint-disable-next-line complexity
export default () => next => action => {
  const { error, payload } = action;

  /**
   *
   * The notification middleware serves to add success and error notifications
   */
  if (isFulfilledAction(action) && payload?.headers) {
    const { alert, alertMessage } = getMessageFromHeaders(payload.headers);
    // `alertKey` values are `timoraApp.<entity>.created`-style translation keys aimed at the customer
    // apps' catalogues; with no catalogue here, show the plain message when the backend sent one.
    if (alertMessage) {
      toast.success(alertMessage);
    } else if (alert) {
      toast.success('Operación realizada con éxito.');
    }
  }

  if (isRejectedAction(action) && isAxiosError(error)) {
    if (error.response) {
      const { response } = error;
      if (response.status === 401) {
        // Ignore, page will be redirected to login.
      } else if (error.config?.url?.endsWith('api/account') || error.config?.url?.endsWith('api/authenticate')) {
        // Ignore, authentication status check and authentication are treated differently.
      } else if (response.status === 0) {
        // connection refused, server not reachable
        addErrorAlert({
          message: 'Server not reachable',
          key: 'error.server.not.reachable',
        });
      } else if (response.status === 404) {
        addErrorAlert({
          message: 'Not found',
          key: 'error.url.not.found',
        });
      } else {
        const { data } = response;
        const problem = isProblemDetails(data) ? data : null;
        if (problem?.fieldErrors) {
          getFieldErrorsToasts(problem.fieldErrors).forEach(message => addErrorAlert(message));
        } else {
          const { error: errorHeader, errorMessage, errorKey } = getMessageFromHeaders(response.headers ?? {});
          if (errorKey) {
            addErrorAlert({ key: errorKey, message: errorMessage ?? errorHeader });
          } else if (errorMessage) {
            addErrorAlert({ message: errorMessage });
          } else if (problem?.message) {
            addErrorAlert({ message: problem.detail, key: problem.message });
          } else if (typeof data === 'string' && data !== '') {
            addErrorAlert({ message: data });
          } else {
            toast.error(data?.detail ?? data?.message ?? data?.error ?? data?.title ?? GENERIC_ERROR);
          }
        }
      }
    } else if (error.config?.url?.endsWith('api/account') && error.config.method === 'get') {
      /* eslint-disable no-console */
      console.log('Authentication Error: Trying to access url api/account with GET.');
    } else {
      addErrorAlert({ message: error.message ?? GENERIC_ERROR });
    }
  } else if (error) {
    addErrorAlert({ message: error.message ?? GENERIC_ERROR });
  }

  return next(action);
};
